import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Crown, Calendar, RefreshCw, Receipt, Sparkles } from "lucide-react";
import { Button } from "./ui/button";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/** 사용자 멤버십 상태 카드 — My Page / My Corner 에 임베드 */
export default function MyMembershipCard() {
  const [data, setData] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("liztv_token");
    if (!token) { setLoading(false); return; }
    const headers = { Authorization: `Bearer ${token}` };
    Promise.all([
      fetch(`${API_URL}/api/paypal/my-membership`, { headers }).then((r) => (r.ok ? r.json() : null)),
      fetch(`${API_URL}/api/paypal/my-subscription`, { headers }).then((r) => (r.ok ? r.json() : { subscription: null })),
    ])
      .then(([m, s]) => { setData(m); setSubscription(s?.subscription); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const cancelSubscription = async () => {
    if (!subscription || !window.confirm("매월 자동결제를 정말 해지하시겠습니까? 다음 결제일부터 청구가 중단됩니다.")) return;
    setCancelling(true);
    try {
      const token = localStorage.getItem("liztv_token");
      const res = await fetch(`${API_URL}/api/paypal/subscriptions/cancel`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ subscription_id: subscription.subscription_id }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "취소 실패");
      }
      const { toast } = await import("sonner");
      toast.success("정기결제가 해지되었습니다.");
      setSubscription(null);
    } catch (err) {
      const { toast } = await import("sonner");
      toast.error(err.message);
    } finally { setCancelling(false); }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-stone-200 p-5 animate-pulse">
        <div className="h-4 w-32 bg-stone-100 rounded mb-3" />
        <div className="h-8 w-56 bg-stone-100 rounded" />
      </div>
    );
  }

  if (!data) return null;

  const { membership_tier, membership_expires_at, days_until_expiration, is_legacy, recent_payments = [] } = data;
  const tier = (membership_tier || "regular").toLowerCase();
  const expiringSoon = days_until_expiration !== null && days_until_expiration <= 30 && days_until_expiration > 0;
  const expired = days_until_expiration === 0 || !membership_expires_at;
  const expDate = membership_expires_at ? new Date(membership_expires_at).toLocaleDateString("ko-KR") : "—";

  const tierStyle = {
    vip:      { name: "VIP",       cls: "from-amber-500 to-yellow-400", icon: Crown },
    premium:  { name: "Premium",   cls: "from-burgundy-500 to-rose-400", icon: Sparkles },
    royal:    { name: "Royal",     cls: "from-amber-500 to-yellow-400", icon: Crown },
    noble:    { name: "Noble",     cls: "from-violet-500 to-pink-400", icon: Sparkles },
    highend:  { name: "High-End",  cls: "from-rose-500 to-amber-400", icon: Sparkles },
    elite:    { name: "Elite",     cls: "from-stone-700 to-stone-500", icon: Sparkles },
    regular:  { name: "Regular",   cls: "from-stone-500 to-stone-400", icon: Sparkles },
  }[tier] || { name: tier, cls: "from-stone-500 to-stone-400", icon: Sparkles };

  const TierIcon = tierStyle.icon;

  return (
    <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden" data-testid="my-membership-card">
      {/* Header strip */}
      <div className={`bg-gradient-to-r ${tierStyle.cls} text-white px-5 py-4 flex items-center justify-between`}>
        <div className="flex items-center gap-2">
          <TierIcon className="w-5 h-5" />
          <span className="font-semibold tracking-wide">{tierStyle.name} 멤버십</span>
          {is_legacy && (
            <span className="ml-2 text-[10px] bg-white/20 px-2 py-0.5 rounded-full uppercase tracking-wider">Legacy</span>
          )}
        </div>
        <span className="text-xs opacity-90">
          {expired ? "만료됨" : expiringSoon ? `D-${days_until_expiration}` : `D-${days_until_expiration ?? "—"}`}
        </span>
      </div>

      <div className="p-5">
        <div className="flex items-center gap-2 text-sm text-stone-600 mb-1">
          <Calendar className="w-4 h-4 text-stone-400" />
          만료일
        </div>
        <p className="text-xl font-bold text-stone-900 mb-3">{expDate}</p>

        {expired ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
            <p className="text-sm text-red-700 font-medium">멤버십이 만료되었습니다.</p>
            <p className="text-xs text-red-600 mt-0.5">갱신하여 모든 혜택을 다시 이용하세요.</p>
          </div>
        ) : expiringSoon ? (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
            <p className="text-sm text-amber-800 font-medium">곧 만료될 예정입니다.</p>
            <p className="text-xs text-amber-700 mt-0.5">30일 안에 갱신해주세요.</p>
          </div>
        ) : null}

        <Link to="/pricing">
          <Button className="w-full bg-burgundy-500 hover:bg-burgundy-600 text-white" data-testid="renew-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            {expired ? "지금 갱신하기" : "멤버십 변경 / 추가 결제"}
          </Button>
        </Link>

        {recent_payments.length > 0 && (
          <div className="mt-5 pt-4 border-t border-stone-100">
            <div className="flex items-center gap-2 text-xs font-semibold text-stone-500 mb-2">
              <Receipt className="w-3.5 h-3.5" />
              최근 결제 내역
            </div>
            <ul className="text-xs space-y-1.5">
              {recent_payments.slice(0, 3).map((p) => (
                <li key={p.id} className="flex items-center justify-between">
                  <span className="text-stone-700 truncate mr-2">{p.label}</span>
                  <span className="flex items-center gap-1.5 shrink-0">
                    <span className="font-medium text-stone-900">${p.amount_usd?.toLocaleString()}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                      p.status === "completed" ? "bg-emerald-100 text-emerald-700" :
                      p.status === "pending"   ? "bg-amber-100 text-amber-700" :
                      "bg-stone-100 text-stone-500"
                    }`}>
                      {p.status === "completed" ? "완료" : p.status === "pending" ? "대기" : p.status}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
